import { useScreenshotContext } from '@components/ScreenshotProvider/useScreenshotContext';
import DateTime from '@lib/utils/datetime';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import ToastModule from '@modules/ToastModule';
import * as Sentry from '@sentry/react';
import {
  Alert,
  Button as AnvilButton,
  Spinner,
  Text as AnvilText,
} from '@servicetitan/anvil2';
import {
  BodyText,
  Button,
  ButtonGroup,
  Headline,
  Icon,
  Link,
  Modal,
  Stack,
  TextArea,
  Tooltip,
} from '@servicetitan/design-system';
import BrowserModule from '@modules/BrowserModule';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { useCallback, useEffect, useState } from 'react';
import FullStoryModule from '@modules/FullStoryModule';
import styles from './BugReportModal.module.css';
import { useReportData } from './useReportData';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import {
  GeolocationDetectionError,
  GeolocationDetectionErrorCode,
  LocaleSource,
  SupportedLocale,
} from '@modules/BrowserModule/types';
import { strings } from './strings';
import UserStatusModule from '@modules/UserStatusModule';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { HelpCenterButton } from '@servicetitan/help-center';
import { useMutation } from '@apollo/client/react';
import { submitUserReportMutation } from './queries';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';

type Props = {
  setOpen: (open: boolean) => void;
};

function base64ToUint8Array(base64String: string) {
  const binaryString = atob(base64String);
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }
  return uint8Array;
}

const BugReportModal: React.FC<Props> = ({ setOpen }) => {
  const auth = useAuth();
  const { image, takeScreenshotOfElement } = useScreenshotContext();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localeCheckError, setLocaleCheckError] = useState<string | null>(null);

  const capturedData = useReportData();
  const {
    [FeatureFlagKeys.featureRefreshUserStatus]: refreshUserStatusEnabled,
    [FeatureFlagKeys.featureMonitorAndResetUserStatus]:
      monitorAndResetUserStatusEnabled,
    [FeatureFlagKeys.featureHelpCenter]: featureHelpCenter,
    [FeatureFlagKeys.featureSendReportToDebugService]:
      featureSendReportToDebugService,
  } = useFeatureFlags();

  const { localeState } = useBrowserState();
  const isLocaleSupported = localeState.locale !== SupportedLocale.Unsupported;
  // True while the locale determination is still in flight for a user who
  // currently appears Unsupported. Scoped to Unsupported so that supported
  // US/CA users whose background GPS watch hasn't fired yet are not affected
  // (their submit button must not be disabled and the Alert must not be shown).
  const localeLoading =
    localeState.locale === SupportedLocale.Unsupported && localeState.isLoading;

  useEffect(() => {
    takeScreenshotOfElement();
    SoftPhoneModule.collectWebPhoneDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear any stale GPS error if the locale transitions back to supported
  // (e.g. background watch fires after a previously-failed user-triggered attempt).
  useEffect(() => {
    if (isLocaleSupported) {
      setLocaleCheckError(null);
    }
  }, [isLocaleSupported]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const getUser = useCallback(() => {
    return (
      auth.user || {
        name: 'Unknown',
        org: {
          id: '',
          name: 'Unknown',
        },
        contactCenterId: '',
        contactCenterUserId: '',
        accountId: '',
      }
    );
  }, [auth]);

  const [submitUserReport] = useMutation(submitUserReportMutation);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      const user = getUser();
      const fileData = JSON.stringify(capturedData, undefined, 2);
      const dt = DateTime(new Date()).format('YYYYMMDDhhmmss');
      const userNameEncoded = encodeURI(auth.user?.name ?? 'Unknown');
      const userFeedback = {
        message,
        name: user.name,
      };
      const userData = {
        captureContext: {
          tags: {
            userName: user.name,
            'org.name': user.org.name,
            'org.id': user.org.id,
            contactCenterId: user.contactCenterId,
            contactCenterUserId: user.contactCenterUserId,
            accountId: user.accountId,
          },
        },
        attachments: [
          {
            filename: `contactcenter-data-${userNameEncoded}-${dt}.json`,
            data: fileData,
            contentType: 'application/json',
          },
          {
            filename: `contactcenter-screenshot-${userNameEncoded}-${dt}.png`,
            data: image
              ? base64ToUint8Array(image.replace('data:image/png;base64,', ''))
              : '',
          },
        ],
      };

      Sentry.captureFeedback(userFeedback, userData);
      if (featureSendReportToDebugService) {
        await submitUserReport({
          variables: {
            input: {
              reportData: fileData,
              fullstoryLink: FullStoryModule.sessionUrl || '',
            },
          },
        });
      }

      setMessage('');
      handleClose();
      ToastModule.showToast({
        title: `Bug report was submitted successfully`,
        status: 'success',
        duration: 5000,
      });

      FullStoryModule.trackEvent('[CCP] Bug Report Submitted', {});
    } catch (error) {
      console.error('Failed to submit bug report', error);
      Sentry.captureException(error);
      ToastModule.showToast({
        title: `Failed to submit bug report`,
        status: 'critical',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    getUser,
    capturedData,
    auth,
    image,
    message,
    handleClose,
    submitUserReport,
    featureSendReportToDebugService,
  ]);

  const disabled =
    !message ||
    !capturedData ||
    submitting ||
    !isLocaleSupported ||
    localeLoading;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    },
    [setMessage],
  );

  /**
   * Triggered by the "I'm in a supported region" button.
   *
   * Delegates to {@link BrowserModule.attemptGeolocationDetection}, which sets
   * `localeState.isLoading` while the position fix is in flight — causing
   * `localeLoading` to become `true`, hiding the Alert and showing a spinner
   * next to the modal title — and then updates `localeState.locale` and
   * `localeState.source` with the GPS result.
   *
   * If the GPS check fails (permission denied, timeout, API unavailable), a
   * {@link GeolocationDetectionError} is caught and a user-facing error message
   * is set on `localeCheckError`, displayed inline below the verify button.
   * The error is cleared on the next attempt.
   */
  const handleVerifyLocale = useCallback(async () => {
    setLocaleCheckError(null);
    try {
      await BrowserModule.attemptGeolocationDetection();
    } catch (error) {
      const errorMessage =
        error instanceof GeolocationDetectionError &&
        error.code === GeolocationDetectionErrorCode.PermissionDenied
          ? strings.verifyLocaleErrorPermissionDenied
          : strings.verifyLocaleErrorFailed;
      setLocaleCheckError(errorMessage);
    }
  }, []);

  const handleRefreshStatus = () => {
    if (monitorAndResetUserStatusEnabled) {
      ModalsModule.openModal(ModalId.ResetAccountModal, {
        open: true,
        onClose: () => ModalsModule.closeActiveModal(),
      });
    } else {
      UserStatusModule.attemptRefreshUserStatus();
    }
    setOpen(false);
  };

  return (
    <Modal.Window open={true} size={ModalSizes.S}>
      <Stack
        className='Modal__header p-x-4 p-y-3'
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <Headline className='m-b-0'>{strings.title}</Headline>
          {localeLoading && <Spinner size='small' />}
        </Stack>
      </Stack>
      <Modal.Content className={styles.bugReportModalContent}>
        <Stack direction='column' spacing={3}>
          {!isLocaleSupported && !localeLoading && (
            <Alert title={strings.unsupportedLocaleErrorTitle} status='danger'>
              <AnvilText>{strings.unsupportedLocaleError}</AnvilText>
              {localeState.source === LocaleSource.Inference && (
                <Stack direction='column' spacing={1}>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <AnvilButton
                      appearance='secondary'
                      size='small'
                      onClick={handleVerifyLocale}
                    >
                      {strings.verifyLocaleLink}
                    </AnvilButton>
                    <Tooltip
                      direction='top'
                      text={strings.verifyLocaleLinkTooltip}
                    >
                      <Icon name='help' size='16' />
                    </Tooltip>
                  </Stack>
                  {localeCheckError && (
                    <AnvilText size='small' className={styles.localeCheckError}>
                      {localeCheckError}
                    </AnvilText>
                  )}
                </Stack>
              )}
            </Alert>
          )}
          {isLocaleSupported && (
            <div>
              {featureHelpCenter ? (
                <>
                  {strings.descriptionWithHelpCenter}{' '}
                  <Link onClick={() => setOpen(false)}>
                    <HelpCenterButton className={styles.helpButton} />
                  </Link>
                </>
              ) : (
                strings.description
              )}
            </div>
          )}
          <TextArea
            placeholder={strings.placeHolder}
            value={message}
            className={styles.messageTextArea}
            autoHeight
            maxRows={5}
            rows={3}
            onChange={handleChange}
            disabled={!isLocaleSupported}
          />
          {refreshUserStatusEnabled && isLocaleSupported && (
            <BodyText>
              {strings.connectionIssueTextBefore}
              <Link color='primary' onClick={handleRefreshStatus}>
                {!monitorAndResetUserStatusEnabled
                  ? strings.connectionIssueTextLink
                  : strings.resetAccountTextLink}
              </Link>
              {strings.connectionIssueTextAfter}
            </BodyText>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={handleClose}>{strings.secondaryText}</Button>
          <Tooltip
            direction='top'
            text={!capturedData ? 'Data loading...' : ''}
          >
            <Button
              onClick={handleSubmit}
              disabled={disabled}
              loading={submitting}
            >
              {strings.primaryText}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Modal.Footer>
    </Modal.Window>
  );
};

export default BugReportModal;
